import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Grid2 as Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import { formatTimeSpan } from '../utils/formatters';
import { Add as AddIcon, Place as PlaceIcon, Share as ShareIcon } from '@mui/icons-material';
import schedulesService from '../services/schedulesService';
import placeServicesService from '../services/placeServicesService';
import placeServiceProfessionalsService from '../services/placeServiceProfessionalsService';
import placesService from '../services/placesService';
import { useSchedules } from '../hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useContextualHint } from '../hooks/useContextualHint';
import AppSnackbar from '../components/ui/AppSnackbar';
import { useLocation } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import ScheduleForm from '../components/forms/ScheduleForm';
import ScheduleFilters from '../components/ScheduleFilters';
import ScheduleCard from '../components/ScheduleCard';
import ShareScheduleDialog from '../components/ShareScheduleDialog';
import ShiftForm from '../components/ShiftForm';
import ScheduleCreationWizard from '../components/schedules/ScheduleCreationWizard';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import { trackEvent, trackError, trackFormAbandonment } from '../utils/tracking';
import PageLayout from '../components/ui/PageLayout';
import { pluralizeWithCount, pluralizePastParticiple } from '../utils/pluralize';

export default function SchedulesPage() {
  const location = useLocation();
  const queryClient = useQueryClient();

  // ✅ Custom Hooks - React Query
  const { 
    schedules, 
    professionals, 
    places, 
    loading, 
    error, 
    reload, 
    create, 
    update, 
    deleteItem 
  } = useSchedules();
  
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const shiftDialog = useDialog();
  const shareDialog = useDialog();
  const { snackbar, showSuccess, showError, showWarning, hideSnackbar } = useSnackbar();

  // Estados adicionais específicos de schedules
  const [placeProfessionals, setPlaceProfessionals] = useState([]);
  const [ScheduleShifts, setAgendaShifts] = useState({});
  const [availablePlaceServices, setAvailablePlaceServices] = useState([]);
  const [shareUrl, setShareUrl] = useState('');
  
  // Estado para wizard de criação de agenda
  const [showCreationWizard, setShowCreationWizard] = useState(false);
  const [selectedPlaceForWizard, setSelectedPlaceForWizard] = useState(null);
  const [showPlaceSelector, setShowPlaceSelector] = useState(false);
  
  // Estado para diálogo de agendamento
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // '' = todos, exceto excluídas

  // Obter dica contextual
  const hint = useContextualHint('schedules', { items: schedules || [], shifts: ScheduleShifts });

  // Abrir wizard via querystring (?wizard=1 ou ?wizard=true) -> abre seletor de local
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wizardParam = params.get('wizard');
    if (wizardParam === '1' || wizardParam === 'true') {
      setShowPlaceSelector(true);
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location.pathname, location.search]);

  // Carregar turnos quando schedules mudar
  useEffect(() => {
    if (schedules?.length > 0) {
      loadShiftsForAgendas(schedules);
    }
  }, [schedules]);

  const loadAllPlaceProfessionals = async (placesData, SchedulesData, professionalsData) => {
    try {
      const allPlaceProfessionals = [];
      
      // Criar placeProfessionals a partir dos professionalIds de cada place
      placesData.forEach(place => {
        if (place.professionalIds && place.professionalIds.length > 0) {
          place.professionalIds.forEach(profId => {
            const professional = professionalsData.find(p => p.id === profId);
            if (professional) {
              // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
              const existingAgenda = SchedulesData.find(
                a => a.placeId === place.id && a.professionalId === profId
              );
              
              allPlaceProfessionals.push({
                id: existingAgenda?.placeProfessionalId || `${place.id}-${profId}`, // ID real ou temporário
                placeId: place.id,
                placeName: place.name,
                professionalId: profId,
                professionalName: professional.name,
                isActive: existingAgenda?.isActive ?? true
              });
            }
          });
        }
      });
      
      setPlaceProfessionals(allPlaceProfessionals);
    } catch (error) {
      console.error('[SchedulesPage] Erro ao criar placeProfessionals:', error);
      setPlaceProfessionals([]);
    }
  };

  const loadShiftsForAgendas = async (SchedulesData) => {
    try {
      const shiftsMap = {};
      await Promise.all(
        SchedulesData.map(async (schedule) => {
          try {
            const shifts = await schedulesService.getShifts(schedule.id, schedule.status === 'Deleted');
            shiftsMap[schedule.id] = shifts;
          } catch (error) {
            console.error(`Erro ao carregar turnos da schedule ${schedule.id}:`, error);
            shiftsMap[schedule.id] = [];
          }
        })
      );
      setAgendaShifts(shiftsMap);
    } catch (error) {
      console.error('Erro ao carregar turnos:', error);
    }
  };

  // ✅ Handlers de CRUD
  // ✅ Callbacks memoizados para cards
  const handleEditAgenda = useCallback((schedule) => {
    formDialog.handleOpen(schedule);
  }, [formDialog]);

  const handleDeleteAgenda = useCallback((schedule) => {
    deleteDialog.handleOpen(schedule);
  }, [deleteDialog]);

  const handleShareAgenda = useCallback((schedule) => {
    const shifts = ScheduleShifts[schedule.id] || [];
    
    if (shifts.length === 0) {
      showWarning('Esta agenda precisa ter turnos e horários configurados para poder ser compartilhada.');
      return;
    }
    
    const url = `https://share.agendario.com.br/schedule/${schedule.id}`;
    setShareUrl(url);
    shareDialog.handleOpen(schedule);
  }, [ScheduleShifts, showWarning, shareDialog]);

  const handleCreate = async (data) => {
    try {
      const result = await create(data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_created', {
        agenda_id: result.id || 'unknown',
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead,
        max_available_days_to_show: data.maxAvailableDaysToShow
      });
      
      showSuccess('Agenda criada com sucesso!');
      await loadShiftsForAgendas(schedules);
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: false,
        professional_id: data.professionalId,
        place_id: data.placeId
      });
      showError('Erro ao criar agenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_updated', {
        agenda_id: formDialog.data.id,
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead
      });
      
      showSuccess('Agenda atualizada com sucesso!');
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: true,
        agenda_id: formDialog.data.id
      });
      showError('Erro ao atualizar agenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Link copiado com sucesso!');
    } catch (error) {
      showError('Erro ao copiar link');
    }
  };

  const handleOpenUrl = () => {
    window.open(shareUrl, '_blank');
  };

  // ✅ Handlers de Turnos (Shifts)
  const handleOpenShift = async (schedule, shift = null) => {
    try {
      // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
      const placeId = schedule.placeId;
      const professionalId = schedule.professionalId;
      const placeProfessionalId = schedule.placeProfessionalId;
      
      if (!placeId) {
        showError('Erro: Local não identificado na agenda');
        return;
      }
      
      if (!professionalId) {
        showError('Erro: Profissional não identificado na agenda');
        return;
      }
      
      // Buscar os serviços do local (pode falhar para agendas compartilhadas — não bloqueia abertura)
      const professionalPlaceServices = [];
      try {
        const allPlaceServices = await placeServicesService.getByPlaceId(placeId);

        for (const placeService of allPlaceServices) {
          try {
            // IMPORTANTE: usar placeService.id (placeServiceId), não serviceId
            // A rota é: /places/{placeId}/services/{placeServiceId}/professionals
            const professionalsData = await placeServiceProfessionalsService.getByPlaceServiceId(placeId, placeService.id) || [];

            // Verificar se o profissional da schedule está nessa lista
            const isProfessionalLinked = professionalsData.some(
              p => p.professionalId === professionalId || p.placeProfessionalId === placeProfessionalId
            );

            if (isProfessionalLinked) {
              professionalPlaceServices.push(placeService);
            }
          } catch (error) {
            console.warn(`Erro ao verificar profissionais do placeService ${placeService.id}:`, error);
            // Continua para próximo serviço
          }
        }
      } catch (servicesError) {
        // Para agendas compartilhadas o local pode não ser acessível — abre o form sem serviços
        console.warn('[SchedulesPage] Serviços do local não disponíveis (agenda compartilhada?):', servicesError);
      }

      setAvailablePlaceServices(professionalPlaceServices);
      shiftDialog.handleOpen({ ...shift, ScheduleId: schedule.id, schedule });
    } catch (error) {
      console.error('Erro ao abrir turno:', error);
      showError('Erro ao abrir turno.');
    }
  };

  const handleShiftSubmit = async (shiftData) => {
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      const shiftId = shiftDialog.data.id;
      
      if (shiftId) {
        await schedulesService.updateShift(ScheduleId, shiftId, shiftData);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno atualizado com sucesso!');
      } else {
        await schedulesService.createShift(ScheduleId, shiftData);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno criado com sucesso!');
      }
      
      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao salvar turno: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleShiftDelete = async (shiftId) => {
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      await schedulesService.deleteShift(ScheduleId, shiftId);
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      showSuccess('Turno excluído com sucesso!');
      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao excluir turno: ' + (error.response?.data?.message || error.message));
    }
  };

  // ✅ Helpers
  const getProfessionalName = (professionalId) => {
    return professionals.find(p => p.id === professionalId)?.name || 'N/A';
  };

  const getPlaceName = (placeId) => {
    return places.find(p => p.id === placeId)?.name || 'N/A';
  };

  const formatShiftDays = (shift) => {
    const days = [];
    if (shift.monday) days.push('Seg');
    if (shift.tuesday) days.push('Ter');
    if (shift.wednesday) days.push('Qua');
    if (shift.thursday) days.push('Qui');
    if (shift.friday) days.push('Sex');
    if (shift.saturday) days.push('Sáb');
    if (shift.sunday) days.push('Dom');
    return days.join(', ');
  };

  // Alias para manter compatibilidade com código existente (props passadas para componentes)
  const formatTime = formatTimeSpan;

  // ✅ Filtros memoizados
  const filteredAgendas = useMemo(() => {
    return (schedules || []).filter((schedule) => {
      const matchesSearchTerm = !searchTerm ||
        schedule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesProfessional = !filterProfessional ||
        schedule.professionalId === filterProfessional;

      const matchesPlace = !filterPlace ||
        schedule.placeId === filterPlace;

      // Filtro de status: por padrão esconde excluídas; ao selecionar um status específico, filtra exatamente
      const matchesStatus = filterStatus
        ? schedule.status === filterStatus
        : schedule.status !== 'Deleted';

      return matchesSearchTerm && matchesProfessional && matchesPlace && matchesStatus;
    });
  }, [schedules, searchTerm, filterProfessional, filterPlace, filterStatus]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProfessional('');
    setFilterPlace('');
    setFilterStatus('');
  };

  // Separar próprias vs compartilhadas (após filtros)
  const ownSchedules = filteredAgendas.filter(s => !s.isShared);
  const sharedSchedules = filteredAgendas.filter(s => s.isShared);

  const renderScheduleGrid = (scheduleList) => (
    <Grid container spacing={3}>
      {scheduleList.map((schedule) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={schedule.id}>
          <ScheduleCard
            schedule={schedule}
            professionalName={schedule.professionalName || 'N/A'}
            placeName={schedule.placeName || 'N/A'}
            shifts={ScheduleShifts[schedule.id] || []}
            canWrite={schedule.canWrite ?? true}
            onEdit={handleEditAgenda}
            onDelete={handleDeleteAgenda}
            onShare={handleShareAgenda}
            onAddShift={() => handleOpenShift(schedule)}
            onEditShift={(shift) => handleOpenShift(schedule, shift)}
            onAppointment={handleOpenAppointmentFromSchedule}
            formatShiftDays={formatShiftDays}
            formatTime={formatTime}
          />
        </Grid>
      ))}
    </Grid>
  );

  // ✅ Handlers para wizard de criação de agenda
  const handleOpenCreationWizard = useCallback((place) => {
    setSelectedPlaceForWizard(place);
    setShowCreationWizard(true);
  }, []);

  const handleCloseCreationWizard = useCallback(() => {
    setShowCreationWizard(false);
    setSelectedPlaceForWizard(null);
  }, []);

  const handleCreationWizardSuccess = useCallback((createdSchedules) => {
    setShowCreationWizard(false);
    setSelectedPlaceForWizard(null);
    showSuccess(`${pluralizeWithCount(createdSchedules.length, 'agenda')} ${pluralizePastParticiple(createdSchedules.length, 'criado', 'criada', 'f')} com sucesso!`);
    reload(); // Recarregar lista de agendas
  }, [showSuccess, reload]);

  // Handler para abrir diálogo de agendamento a partir de um schedule
  const handleOpenAppointmentFromSchedule = useCallback((schedule) => {
    setAppointmentPrefillConfig({
      professionalId: schedule.professionalId,
      placeId: schedule.placeId,
    });
    setAppointmentDialogOpen(true);
  }, []);

  // Handler para fechar diálogo de agendamento
  const handleCloseAppointmentDialog = useCallback(() => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, []);

  // Handler para sucesso do agendamento
  const handleAppointmentSuccess = useCallback((appointment) => {
    showSuccess('Agendamento criado com sucesso!');
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, [showSuccess]);

  // Obter profissionais de um local (com serviceIds do vínculo PlaceProfessional)
  const getProfessionalsForPlace = useCallback((placeId) => {
    // Buscar o place pelo ID
    const place = places.find(p => p.id === placeId);
    if (!place || !place.professionalIds) {
      return [];
    }
    
    // Mapear professionalIds para objetos de profissional com serviceIds
    return place.professionalIds
      .map(profId => {
        const prof = professionals.find(p => p.id === profId);
        // Buscar serviceIds do vínculo PlaceProfessional (padrão BFF normalizado)
        const placeProfLink = place.professionals?.find(pp => pp.professionalId === profId);
        return prof ? { 
          id: prof.id, 
          name: prof.name,
          serviceIds: placeProfLink?.serviceIds || []
        } : null;
      })
      .filter(Boolean);
  }, [places, professionals]);

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando agendas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <PageLayout
      themeKey="schedules"
      actionLabel="Nova Agenda"
      onAction={() => setShowPlaceSelector(true)}
      actionDisabled={places.length === 0}
      hint={hint}
      onHintAction={() => formDialog.handleOpen(null)}
      infoBanner="Configure agendas automáticas vinculando profissionais a locais. A geração automática cria horários disponíveis com base nos turnos configurados."
    >
      {/* Filtros */}
      <ScheduleFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterProfessional={filterProfessional}
        onProfessionalChange={setFilterProfessional}
        filterPlace={filterPlace}
        onPlaceChange={setFilterPlace}
        filterStatus={filterStatus}
        onStatusChange={setFilterStatus}
        professionals={professionals}
        places={places}
        onClearFilters={clearFilters}
      />

      {/* Empty State */}
      {filteredAgendas.length === 0 ? (
        <EmptyState
          icon={() => <Typography fontSize={64}>📅</Typography>}
          title={schedules.length === 0 ? 'Nenhuma agenda cadastrada' : 'Nenhuma agenda encontrada'}
          message={
            schedules.length === 0
              ? 'Clique em "Nova Agenda" para começar.'
              : 'Tente ajustar os filtros para encontrar suas agendas.'
          }
          action={
            schedules.length === 0 && places.length > 0 && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowPlaceSelector(true)}>
                Nova Agenda
              </Button>
            )
          }
        />
      ) : (
        <>
          {/* Minhas Agendas */}
          {ownSchedules.length > 0 && (
            <Box mb={sharedSchedules.length > 0 ? 4 : 0}>
              {sharedSchedules.length > 0 && (
                <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                  Minhas Agendas ({ownSchedules.length})
                </Typography>
              )}
              {renderScheduleGrid(ownSchedules)}
            </Box>
          )}

          {/* Compartilhadas comigo */}
          {sharedSchedules.length > 0 && (
            <Box mb={4}>
              {ownSchedules.length > 0 && <Divider sx={{ mb: 3 }} />}
              <Typography variant="h6" fontWeight="bold" color="info.main" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShareIcon fontSize="small" />
                Compartilhadas comigo ({sharedSchedules.length})
              </Typography>
              {renderScheduleGrid(sharedSchedules)}
            </Box>
          )}
        </>
      )}

      {/* schedule Form Dialog */}
      <ScheduleForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
        professionals={professionals}
        places={places}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/schedules/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Share Dialog */}
      <ShareScheduleDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        shareUrl={shareUrl}
        onCopy={handleCopyUrl}
        onOpen={handleOpenUrl}
      />

      {/* Shift Form Dialog */}
      <ShiftForm
        open={shiftDialog.open}
        onClose={shiftDialog.handleClose}
        onSubmit={handleShiftSubmit}
        onDelete={handleShiftDelete}
        shift={shiftDialog.data}
        ScheduleId={shiftDialog.data?.ScheduleId}
        availablePlaceServices={availablePlaceServices}
      />

      {/* Dialog de seleção de local para nova agenda */}
      <Dialog 
        open={showPlaceSelector} 
        onClose={() => setShowPlaceSelector(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Selecione o Local</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escolha o local onde deseja criar agendas de atendimento:
          </Typography>
          <List>
            {places.map(place => {
              const profs = getProfessionalsForPlace(place.id);
              return (
                <ListItem key={place.id} disablePadding>
                  <ListItemButton 
                    onClick={() => {
                      setShowPlaceSelector(false);
                      handleOpenCreationWizard({
                        ...place,
                        professionals: profs,
                      });
                    }}
                    disabled={profs.length === 0}
                  >
                    <ListItemIcon>
                      <PlaceIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={place.name}
                      secondary={profs.length > 0 
                        ? `${pluralizeWithCount(profs.length, 'profissional', 'profissionais')}: ${profs.map(p => p.name).join(', ')}`
                        : 'Nenhum profissional vinculado'
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          {places.length === 0 && (
            <Alert severity="warning">
              Você precisa criar um local primeiro antes de criar agendas.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPlaceSelector(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Wizard de criação de agenda */}
      {showCreationWizard && selectedPlaceForWizard && (
        <ScheduleCreationWizard
          open={showCreationWizard}
          onClose={handleCloseCreationWizard}
          onSuccess={handleCreationWizardSuccess}
          placeName={selectedPlaceForWizard.name}
          placeId={selectedPlaceForWizard.id}
          professionals={selectedPlaceForWizard.professionals || []}
        />
      )}

      {/* Diálogo de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* Snackbar */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
